/**
 * AdminOnboardingTab — Gestão de onboarding por cliente (CVM 30)
 * Sprint 10: Enhanced UX — visual pipeline, progress bars, step tracking,
 * real action handlers with notifications, expandable client details.
 *
 * Modo REVISÃO/APROVAÇÃO: admin revisa preenchimentos do cliente,
 * pode aprovar, solicitar preenchimento ou enviar lembrete.
 * Não permite preenchimento pelo admin.
 *
 * v2: Layout optimization — compact summary cards, inline progress,
 * tighter row height, consistent action hierarchy.
 */
import { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Card, STitle, Badge } from "./adminUtils.jsx";
import { RADIUS, SP, TRANSITION } from "../shared/theme.js";
import { flexRow, flexCol, flexBetween } from "../shared/styles.js";
import { createNotification } from "../services/notifications.ts";
import { insertCRMEvent } from "../services/crm.ts";
import { logAudit } from "../supabaseClient.js";
import { logWarn } from "../shared/logger.js";

const PAGE_SIZE = 20;

// ── Pipeline stages ──────────────────────────────────────────
const PIPELINE = [
  { id: "pendente", label: "Não Iniciado", icon: "⚠️", color: "#DC2626", bgLight: "#FEE2E2" },
  { id: "parcial", label: "Em Andamento", icon: "📝", color: "#D97706", bgLight: "#FEF3C7" },
  {
    id: "completo",
    label: "Aguardando Aprovação",
    icon: "⏳",
    color: "#2563EB",
    bgLight: "#DBEAFE",
  },
  { id: "aprovado", label: "Aprovado", icon: "✅", color: "#059669", bgLight: "#D1FAE5" },
];

const STATUS_OPTS = [
  { value: "all", label: "Todos" },
  { value: "completo", label: "⏳ Aguardando" },
  { value: "parcial", label: "📝 Parcial" },
  { value: "pendente", label: "⚠ Pendente" },
  { value: "aprovado", label: "✅ Aprovado" },
];

const PERFIL_OPTS = [
  { value: "all", label: "Todos os perfis" },
  { value: "Conservador", label: "Conservador" },
  { value: "Moderado", label: "Moderado" },
  { value: "Arrojado", label: "Arrojado" },
  { value: "Agressivo", label: "Agressivo" },
  { value: "Pendente", label: "Pendente" },
];

const SORT_OPTS = [
  { value: "name", label: "Nome A-Z" },
  { value: "status", label: "Status" },
  { value: "date", label: "Data preenchimento" },
  { value: "score", label: "Score" },
];

// ── Helpers ──────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } catch {
    return "–";
  }
}

function daysSince(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  } catch {
    return null;
  }
}

function getStatusOrder(status) {
  const order = { pendente: 0, parcial: 1, completo: 2, aprovado: 3 };
  return order[status] ?? 0;
}

// ── Onboarding steps for progress indicator ──────────────────
const STEPS = [
  { key: "dados", label: "Dados Pessoais", short: "Dados" },
  { key: "perfil", label: "Perfil de Risco", short: "Perfil" },
  { key: "objetivos", label: "Objetivos", short: "Obj." },
  { key: "experiencia", label: "Experiência", short: "Exp." },
  { key: "assinatura", label: "Assinatura", short: "Assin." },
];

function computeSteps(suit) {
  if (!suit) return { completed: 0, total: STEPS.length, steps: STEPS.map(() => false) };
  const steps = [
    !!(suit.nome || suit.cpf),
    !!(suit.perfilResult && suit.perfilResult !== "Pendente"),
    !!(suit.scoreSuit != null),
    !!suit.dataPreenchimento,
    !!(suit.assinaturaCliente && suit.assinaturaConsultor),
  ];
  return { completed: steps.filter(Boolean).length, total: STEPS.length, steps };
}

const PERFIL_COLORS = {
  Conservador: "#16A34A",
  Moderado: "#D97706",
  Arrojado: "#7C3AED",
  Agressivo: "#DC2626",
};

// ── Component ────────────────────────────────────────────────
export default function AdminOnboardingTab({
  dynamicClients,
  suitabilityDB,
  setSelectedClient,
  setEditOnboarding,
  onApprove,
  T,
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [perfilFilter, setPerfilFilter] = useState("all");
  const [sortBy, setSortBy] = useState("status");
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // "table" | "pipeline"

  // ── Filtered + sorted clients ──
  const filtered = useMemo(() => {
    let list = dynamicClients;

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(
        (c) => (c.name || "").toLowerCase().includes(q) || (c.code || "").toLowerCase().includes(q)
      );
    }

    if (statusFilter !== "all") {
      list = list.filter((c) => {
        const s = suitabilityDB[c.id]?.status || "pendente";
        return s === statusFilter;
      });
    }

    if (perfilFilter !== "all") {
      list = list.filter((c) => {
        const p = suitabilityDB[c.id]?.perfilResult || "Pendente";
        return p === perfilFilter;
      });
    }

    // Sort
    list = [...list].sort((a, b) => {
      const sa = suitabilityDB[a.id] || {};
      const sb = suitabilityDB[b.id] || {};
      switch (sortBy) {
        case "name":
          return (a.name || "").localeCompare(b.name || "");
        case "status":
          return getStatusOrder(sa.status || "pendente") - getStatusOrder(sb.status || "pendente");
        case "date": {
          const da = sa.dataPreenchimento ? new Date(sa.dataPreenchimento).getTime() : 0;
          const db = sb.dataPreenchimento ? new Date(sb.dataPreenchimento).getTime() : 0;
          return db - da;
        }
        case "score":
          return (sb.scoreSuit || 0) - (sa.scoreSuit || 0);
        default:
          return 0;
      }
    });

    return list;
  }, [dynamicClients, search, statusFilter, perfilFilter, sortBy, suitabilityDB]);

  // Reset page on filter change
  const resetPage = () => setPage(0);
  const setSearchR = (v) => {
    setSearch(v);
    resetPage();
  };
  const setStatusR = (v) => {
    setStatusFilter(v);
    resetPage();
  };
  const setPerfilR = (v) => {
    setPerfilFilter(v);
    resetPage();
  };
  const setSortR = (v) => {
    setSortBy(v);
    resetPage();
  };

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageClients = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ── Pipeline counts ──
  const pipelineCounts = useMemo(() => {
    const counts = { pendente: 0, parcial: 0, completo: 0, aprovado: 0 };
    dynamicClients.forEach((c) => {
      const s = suitabilityDB[c.id]?.status || "pendente";
      if (counts[s] !== undefined) counts[s]++;
      else counts.pendente++;
    });
    return counts;
  }, [dynamicClients, suitabilityDB]);

  // ── Action handlers ──
  const showFeedback = useCallback((clientId, type) => {
    setActionFeedback({ clientId, type, time: Date.now() });
    setTimeout(() => setActionFeedback(null), 3000);
  }, []);

  const handleSolicitarPreenchimento = useCallback(
    async (clientId, clientName) => {
      setActionLoading(clientId);
      try {
        await createNotification(
          clientId,
          "onboarding",
          "Solicitação de Preenchimento",
          `Por favor, preencha seu formulário de suitability para dar continuidade ao seu onboarding.`
        );
        await insertCRMEvent({
          client_id: clientId,
          tipo_evento: "email",
          descricao: `Solicitação de preenchimento do onboarding enviada para ${clientName}`,
          data_evento: new Date().toISOString(),
        });
        await logAudit("onboarding_request_sent", { client_id: clientId });
        showFeedback(clientId, "solicitado");
      } catch (err) {
        logWarn("AdminOnboardingTab", "Erro ao solicitar preenchimento", { error: err?.message });
        showFeedback(clientId, "erro");
      } finally {
        setActionLoading(null);
      }
    },
    [showFeedback]
  );

  const handleLembrarCliente = useCallback(
    async (clientId, clientName) => {
      setActionLoading(clientId);
      try {
        await createNotification(
          clientId,
          "onboarding",
          "Lembrete de Onboarding",
          `Olá! Seu formulário de suitability ainda está pendente. Complete-o para que possamos dar continuidade ao seu planejamento.`
        );
        await insertCRMEvent({
          client_id: clientId,
          tipo_evento: "contato",
          descricao: `Lembrete de onboarding enviado para ${clientName}`,
          data_evento: new Date().toISOString(),
        });
        await logAudit("onboarding_reminder_sent", { client_id: clientId });
        showFeedback(clientId, "lembrete");
      } catch (err) {
        logWarn("AdminOnboardingTab", "Erro ao enviar lembrete", { error: err?.message });
        showFeedback(clientId, "erro");
      } finally {
        setActionLoading(null);
      }
    },
    [showFeedback]
  );

  const handleAprovar = useCallback(
    async (clientId, clientName) => {
      if (
        !window.confirm(
          `Aprovar o onboarding de ${clientName}? Esta ação encerra o processo de onboarding.`
        )
      )
        return;
      setActionLoading(clientId);
      try {
        showFeedback(clientId, "aprovado");
        if (onApprove) await onApprove(clientId, clientName);
      } catch (err) {
        logWarn("AdminOnboardingTab", "Erro ao aprovar", { error: err?.message });
      } finally {
        setActionLoading(null);
      }
    },
    [showFeedback, onApprove]
  );

  // ── Styles ──
  const inputStyle = {
    padding: "7px 12px",
    borderRadius: RADIUS.sm,
    border: `1px solid ${T.border}`,
    fontSize: 12,
    color: T.text,
    background: T.card,
    outline: "none",
  };
  const selectStyle = { ...inputStyle, cursor: "pointer", minWidth: 110 };
  const btnBase = {
    padding: "5px 10px",
    borderRadius: RADIUS.sm,
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 600,
    border: "none",
    transition: TRANSITION.fast,
    whiteSpace: "nowrap",
  };
  const pageBtnStyle = {
    padding: "6px 14px",
    borderRadius: RADIUS.sm,
    border: `1px solid ${T.border}`,
    background: T.bg,
    color: T.text,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  };
  const viewBtn = (active) => ({
    padding: "6px 14px",
    borderRadius: RADIUS.sm,
    border: `1px solid ${active ? T.info : T.border}`,
    background: active ? T.infoBg || "#DBEAFE" : T.card,
    color: active ? T.info : T.sub,
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    transition: TRANSITION.fast,
  });

  // ── Render helpers ──

  // Compact inline progress: horizontal bar + step dots in single row
  const renderProgressInline = (suit) => {
    const { completed, total, steps } = computeSteps(suit);
    const pct = total > 0 ? (completed / total) * 100 : 0;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 120 }}>
        {/* Progress bar */}
        <div style={{ flex: 1, minWidth: 48 }}>
          <div
            style={{
              height: 5,
              borderRadius: 3,
              background: T.border,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                borderRadius: 3,
                background: pct === 100 ? "#16A34A" : pct > 50 ? "#2563EB" : "#D97706",
                transition: "width .3s ease",
              }}
            />
          </div>
        </div>
        {/* Step dots */}
        <div style={{ display: "flex", gap: 2 }}>
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              title={s.label}
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: steps[i] ? "#16A34A" : T.border,
                color: steps[i] ? "#fff" : T.muted,
                fontSize: 7,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
              }}
            >
              {steps[i] ? "✓" : i + 1}
            </div>
          ))}
        </div>
        {/* Count */}
        <span style={{ fontSize: 10, color: T.sub, fontWeight: 600, whiteSpace: "nowrap" }}>
          {completed}/{total}
        </span>
      </div>
    );
  };

  // Full progress for pipeline view and expanded detail
  const renderProgressFull = (suit) => {
    const { completed, total, steps } = computeSteps(suit);
    const pct = total > 0 ? (completed / total) * 100 : 0;
    return (
      <div style={{ width: "100%" }}>
        <div style={{ ...flexBetween, marginBottom: 4 }}>
          <span style={{ fontSize: 10, color: T.sub, fontWeight: 600 }}>
            {completed}/{total} etapas
          </span>
          <span style={{ fontSize: 10, color: T.muted }}>{pct.toFixed(0)}%</span>
        </div>
        <div style={{ height: 5, borderRadius: 3, background: T.border, overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              borderRadius: 3,
              background: pct === 100 ? "#16A34A" : pct > 50 ? "#2563EB" : "#D97706",
              transition: "width .3s ease",
            }}
          />
        </div>
        <div style={{ ...flexRow, gap: 3, marginTop: 5 }}>
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              title={s.label}
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: steps[i] ? "#16A34A" : T.border,
                color: steps[i] ? "#fff" : T.muted,
                fontSize: 7,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {steps[i] ? "✓" : i + 1}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStatusBadge = (status) => {
    const stage = PIPELINE.find((p) => p.id === status) || PIPELINE[0];
    return <Badge text={`${stage.icon} ${stage.label}`} color={stage.color} bg={stage.bgLight} />;
  };

  const renderActions = (c, suit, status) => {
    const isAprovado = status === "aprovado";
    const isComplete = status === "completo";
    const isParcial = status === "parcial";
    const isPendente = !isComplete && !isParcial && !isAprovado;
    const isLoading = actionLoading === c.id;
    const fb = actionFeedback?.clientId === c.id ? actionFeedback : null;

    if (fb) {
      const fbMap = {
        solicitado: { text: "📧 Enviado!", color: "#2563EB", bg: "#EFF6FF" },
        lembrete: { text: "🔔 Lembrete!", color: "#D97706", bg: "#FEF3C7" },
        aprovado: { text: "✅ Aprovado!", color: "#16A34A", bg: "#DCFCE7" },
        erro: { text: "❌ Erro", color: "#DC2626", bg: "#FEE2E2" },
      };
      const f = fbMap[fb.type] || fbMap.erro;
      return (
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: f.color,
            padding: "3px 8px",
            background: f.bg,
            borderRadius: RADIUS.sm,
          }}
        >
          {f.text}
        </span>
      );
    }

    return (
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        {/* Profile icon always visible */}
        <button
          onClick={() => setSelectedClient(c.id)}
          title="Ver perfil"
          style={{
            ...btnBase,
            padding: "4px 7px",
            background: T.bg,
            color: T.text,
            border: `1px solid ${T.border}`,
          }}
        >
          👤
        </button>

        {/* Primary action by status — single prominent button */}
        {isComplete && (
          <>
            <button
              onClick={() => setEditOnboarding(c.id)}
              title="Revisar"
              disabled={isLoading}
              style={{
                ...btnBase,
                background: T.text,
                color: "#fff",
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              👁
            </button>
            <button
              onClick={() => handleAprovar(c.id, c.name)}
              title="Aprovar"
              disabled={isLoading}
              style={{
                ...btnBase,
                background: "#16A34A",
                color: "#fff",
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              ✓ Aprovar
            </button>
          </>
        )}

        {isParcial && (
          <>
            <button
              onClick={() => setEditOnboarding(c.id)}
              title="Revisar"
              disabled={isLoading}
              style={{
                ...btnBase,
                background: T.text,
                color: "#fff",
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              👁
            </button>
            <button
              onClick={() => handleLembrarCliente(c.id, c.name)}
              title="Lembrar"
              disabled={isLoading}
              style={{
                ...btnBase,
                background: "#D97706",
                color: "#fff",
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              {isLoading ? "…" : "🔔 Lembrar"}
            </button>
          </>
        )}

        {isPendente && (
          <>
            <button
              onClick={() => handleSolicitarPreenchimento(c.id, c.name)}
              title="Solicitar"
              disabled={isLoading}
              style={{
                ...btnBase,
                background: "#2563EB",
                color: "#fff",
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              {isLoading ? "…" : "📧 Solicitar"}
            </button>
            <button
              onClick={() => handleLembrarCliente(c.id, c.name)}
              title="Lembrar"
              disabled={isLoading}
              style={{
                ...btnBase,
                background: "#D97706",
                color: "#fff",
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              {isLoading ? "…" : "🔔 Lembrar"}
            </button>
          </>
        )}
      </div>
    );
  };

  // ── Pipeline view ──
  const renderPipelineView = () => (
    <div
      style={{ display: "grid", gridTemplateColumns: `repeat(${PIPELINE.length}, 1fr)`, gap: 14 }}
    >
      {PIPELINE.map((stage) => {
        const clients = dynamicClients.filter((c) => {
          const s = suitabilityDB[c.id]?.status || "pendente";
          return s === stage.id;
        });
        return (
          <div
            key={stage.id}
            style={{
              background: T.card,
              border: `1px solid ${T.border}`,
              borderRadius: RADIUS.md,
              overflow: "hidden",
            }}
          >
            {/* Stage header */}
            <div
              style={{
                padding: "10px 14px",
                background: stage.bgLight,
                borderBottom: `2px solid ${stage.color}`,
                ...flexBetween,
              }}
            >
              <div style={{ ...flexRow, gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 14 }}>{stage.icon}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: stage.color }}>
                  {stage.label}
                </span>
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: "#fff",
                  background: stage.color,
                  borderRadius: 10,
                  padding: "2px 8px",
                  minWidth: 22,
                  textAlign: "center",
                }}
              >
                {clients.length}
              </span>
            </div>

            {/* Client cards */}
            <div style={{ padding: 8, ...flexCol, gap: 6, maxHeight: 420, overflowY: "auto" }}>
              {clients.length === 0 ? (
                <div
                  style={{ fontSize: 11, color: T.muted, textAlign: "center", padding: "20px 0" }}
                >
                  Nenhum cliente
                </div>
              ) : (
                clients.map((c) => {
                  const suit = suitabilityDB[c.id] || {};
                  const daysAgo = daysSince(suit.dataPreenchimento);
                  return (
                    <div
                      key={c.id}
                      style={{
                        padding: "8px 10px",
                        background: T.bg,
                        borderRadius: RADIUS.sm,
                        border: `1px solid ${T.border}`,
                        cursor: "pointer",
                        transition: TRANSITION.fast,
                      }}
                      onClick={() =>
                        stage.id === "completo" ? setEditOnboarding(c.id) : setSelectedClient(c.id)
                      }
                    >
                      <div
                        style={{
                          ...flexBetween,
                          marginBottom: 6,
                          alignItems: "flex-start",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: T.text,
                              lineHeight: 1.3,
                            }}
                          >
                            {c.name}
                          </div>
                          {c.code && (
                            <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>
                              {c.code}
                            </div>
                          )}
                        </div>
                        {suit.perfilResult && suit.perfilResult !== "Pendente" && (
                          <Badge
                            text={suit.perfilResult}
                            color={PERFIL_COLORS[suit.perfilResult] || "#94A3B8"}
                          />
                        )}
                      </div>
                      {renderProgressFull(suit)}
                      {daysAgo != null && (
                        <div style={{ fontSize: 10, color: T.muted, marginTop: 4 }}>
                          Preenchido há {daysAgo}d
                        </div>
                      )}
                      <div style={{ marginTop: 6 }}>{renderActions(c, suit, stage.id)}</div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  // ── Expanded row detail ──
  const renderExpandedDetail = (c, suit) => {
    const daysAgo = daysSince(suit.dataPreenchimento);
    return (
      <tr key={`${c.id}_exp`}>
        <td colSpan={8} style={{ padding: 0 }}>
          <div
            style={{
              padding: "14px 20px",
              background: T.surface || T.bg,
              borderBottom: `1px solid ${T.border}`,
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
              {/* Progress detail */}
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: T.sub,
                    marginBottom: 8,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                  }}
                >
                  Progresso do Onboarding
                </div>
                {STEPS.map((s, i) => {
                  const done = computeSteps(suit).steps[i];
                  return (
                    <div
                      key={s.key}
                      style={{ ...flexRow, gap: 8, marginBottom: 5, alignItems: "center" }}
                    >
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          background: done ? "#16A34A" : T.border,
                          color: done ? "#fff" : T.muted,
                          fontSize: 9,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {done ? "✓" : "–"}
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          color: done ? T.text : T.muted,
                          fontWeight: done ? 600 : 400,
                        }}
                      >
                        {s.label}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Client info */}
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: T.sub,
                    marginBottom: 8,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                  }}
                >
                  Informações
                </div>
                {[
                  { label: "Email", value: c.email || "–" },
                  {
                    label: "Score",
                    value: suit.scoreSuit != null ? `${suit.scoreSuit}/${suit.scoreMax}` : "–",
                  },
                  {
                    label: "Preenchido",
                    value:
                      daysAgo != null
                        ? `${fmtDate(suit.dataPreenchimento)} (${daysAgo}d atrás)`
                        : "–",
                  },
                  {
                    label: "Assin. Cliente",
                    value: suit.assinaturaCliente ? "✅ Sim" : "❌ Não",
                  },
                  {
                    label: "Assin. Consultor",
                    value: suit.assinaturaConsultor ? "✅ Sim" : "❌ Não",
                  },
                ].map((item) => (
                  <div key={item.label} style={{ ...flexBetween, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: T.muted }}>{item.label}</span>
                    <span style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Quick actions */}
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: T.sub,
                    marginBottom: 8,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                  }}
                >
                  Ações Rápidas
                </div>
                <div style={{ ...flexCol, gap: 6 }}>
                  <button
                    onClick={() => setEditOnboarding(c.id)}
                    style={{
                      ...btnBase,
                      background: T.text,
                      color: "#fff",
                      width: "100%",
                      padding: "7px 12px",
                      fontSize: 12,
                    }}
                  >
                    👁 Abrir Revisão Completa
                  </button>
                  <button
                    onClick={() => setSelectedClient(c.id)}
                    style={{
                      ...btnBase,
                      background: T.bg,
                      color: T.text,
                      border: `1px solid ${T.border}`,
                      width: "100%",
                      padding: "7px 12px",
                      fontSize: 12,
                    }}
                  >
                    👤 Ver Perfil do Cliente
                  </button>
                </div>
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div>
      {/* ── Pipeline summary — compact horizontal strip ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${PIPELINE.length}, 1fr)`,
          gap: 10,
          marginBottom: 18,
        }}
      >
        {PIPELINE.map((stage) => {
          const count = pipelineCounts[stage.id] || 0;
          const pct = dynamicClients.length > 0 ? (count / dynamicClients.length) * 100 : 0;
          const isActive = statusFilter === stage.id;
          return (
            <Card
              key={stage.id}
              T={T}
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                border: isActive ? `2px solid ${stage.color}` : undefined,
                transform: isActive ? "translateY(-2px)" : undefined,
                transition: TRANSITION.fast,
              }}
              onClick={() => setStatusR(isActive ? "all" : stage.id)}
            >
              <div style={{ ...flexRow, gap: 10, alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 18 }}>{stage.icon}</span>
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: T.sub,
                      textTransform: "uppercase",
                      letterSpacing: 0.6,
                      margin: 0,
                    }}
                  >
                    {stage.label}
                  </p>
                  <p
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: stage.color,
                      margin: 0,
                      lineHeight: 1.2,
                    }}
                  >
                    {count}
                  </p>
                </div>
              </div>
              {/* Progress bar */}
              <div style={{ height: 3, borderRadius: 2, background: T.border, overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${pct}%`,
                    borderRadius: 2,
                    background: stage.color,
                    transition: "width .3s ease",
                  }}
                />
              </div>
              <div style={{ fontSize: 10, color: T.muted, marginTop: 3, textAlign: "right" }}>
                {pct.toFixed(0)}%
              </div>
            </Card>
          );
        })}
      </div>

      {/* ── View toggle + filters ── */}
      <Card T={T} style={{ padding: "18px 20px" }}>
        <div style={{ ...flexBetween, marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <STitle
            title="Gestão de Onboarding"
            sub="CVM 30 — Revisão e aprovação de suitability"
            T={T}
          />
          <div style={{ ...flexRow, gap: 6 }}>
            <button onClick={() => setViewMode("table")} style={viewBtn(viewMode === "table")}>
              📋 Tabela
            </button>
            <button
              onClick={() => setViewMode("pipeline")}
              style={viewBtn(viewMode === "pipeline")}
            >
              📊 Pipeline
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <input
            type="text"
            placeholder="Buscar nome ou código…"
            value={search}
            onChange={(e) => setSearchR(e.target.value)}
            style={{ ...inputStyle, flex: "1 1 180px", minWidth: 160 }}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusR(e.target.value)}
            style={selectStyle}
          >
            {STATUS_OPTS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={perfilFilter}
            onChange={(e) => setPerfilR(e.target.value)}
            style={selectStyle}
          >
            {PERFIL_OPTS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {viewMode === "table" && (
            <select value={sortBy} onChange={(e) => setSortR(e.target.value)} style={selectStyle}>
              {SORT_OPTS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          )}
          {(search || statusFilter !== "all" || perfilFilter !== "all") && (
            <div style={{ ...flexRow, gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: T.muted }}>
                {filtered.length} de {dynamicClients.length}
              </span>
              <button
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                  setPerfilFilter("all");
                  setPage(0);
                }}
                style={{
                  ...btnBase,
                  background: T.bg,
                  color: T.muted,
                  border: `1px solid ${T.border}`,
                  fontSize: 10,
                }}
              >
                ✕ Limpar
              </button>
            </div>
          )}
        </div>

        {/* ── Pipeline view ── */}
        {viewMode === "pipeline" && renderPipelineView()}

        {/* ── Table view — optimized: merged columns, compact rows ── */}
        {viewMode === "table" && (
          <>
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {[
                      "",
                      "Cliente",
                      "Perfil / Score",
                      "Progresso",
                      "Status",
                      "Preenchido",
                      "Ações",
                    ].map((h) => (
                      <th
                        key={h}
                        scope="col"
                        style={{
                          textAlign: "left",
                          padding: "8px 10px",
                          borderBottom: `2px solid ${T.border}`,
                          color: T.sub,
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 0.6,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageClients.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        style={{
                          padding: "30px 12px",
                          textAlign: "center",
                          color: T.muted,
                          fontSize: 13,
                        }}
                      >
                        {search || statusFilter !== "all" || perfilFilter !== "all"
                          ? "Nenhum cliente encontrado com esses filtros."
                          : "Nenhum cliente cadastrado."}
                      </td>
                    </tr>
                  ) : (
                    pageClients.map((c, i) => {
                      const suit = suitabilityDB[c.id] || {};
                      const status = suit.status || "pendente";
                      const isExpanded = expandedId === c.id;

                      return [
                        <tr
                          key={c.id}
                          style={{
                            background: i % 2 === 0 ? T.row1 : T.row2,
                            cursor: "pointer",
                            transition: TRANSITION.fast,
                          }}
                          onClick={() => setExpandedId(isExpanded ? null : c.id)}
                        >
                          {/* Expand chevron */}
                          <td style={{ padding: "8px 4px 8px 10px", width: 20 }}>
                            <span
                              style={{
                                display: "inline-block",
                                fontSize: 9,
                                color: T.muted,
                                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                transition: "transform .15s ease",
                              }}
                            >
                              ▶
                            </span>
                          </td>

                          {/* Client name + code merged */}
                          <td style={{ padding: "8px 10px" }}>
                            <div style={{ fontWeight: 700, color: T.text, fontSize: 12 }}>
                              {c.name}
                            </div>
                            <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>
                              {c.code || "–"}
                            </div>
                          </td>

                          {/* Profile + Score merged */}
                          <td style={{ padding: "8px 10px" }}>
                            <div style={{ ...flexRow, gap: 6, alignItems: "center" }}>
                              <Badge
                                text={suit.perfilResult || "Pendente"}
                                color={PERFIL_COLORS[suit.perfilResult] || "#94A3B8"}
                              />
                              {suit.scoreSuit != null && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: T.text,
                                  }}
                                >
                                  {suit.scoreSuit}/{suit.scoreMax}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Progress — compact inline */}
                          <td
                            style={{ padding: "8px 10px", minWidth: 180 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {renderProgressInline(suit)}
                          </td>

                          {/* Status badge */}
                          <td style={{ padding: "8px 10px" }}>{renderStatusBadge(status)}</td>

                          {/* Date */}
                          <td style={{ padding: "8px 10px", fontSize: 11, color: T.sub }}>
                            {fmtDate(suit.dataPreenchimento)}
                          </td>

                          {/* Actions */}
                          <td style={{ padding: "8px 10px" }} onClick={(e) => e.stopPropagation()}>
                            {renderActions(c, suit, status)}
                          </td>
                        </tr>,
                        isExpanded && renderExpandedDetail(c, suit),
                      ];
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 12,
                  paddingTop: 10,
                  borderTop: `1px solid ${T.border}`,
                }}
              >
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  style={{ ...pageBtnStyle, opacity: page === 0 ? 0.4 : 1 }}
                >
                  ← Anterior
                </button>
                <span style={{ fontSize: 12, color: T.sub }}>
                  {page + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  style={{ ...pageBtnStyle, opacity: page >= totalPages - 1 ? 0.4 : 1 }}
                >
                  Próxima →
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* ── CVM 30 info box ── */}
      <div
        style={{
          marginTop: 16,
          background: T.infoBg || "#EFF6FF",
          border: `1px solid ${T.info ? T.info + "33" : "#BFDBFE"}`,
          borderRadius: RADIUS.md,
          padding: "10px 16px",
          ...flexRow,
          gap: 8,
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: 13, flexShrink: 0 }}>ℹ️</span>
        <p style={{ fontSize: 11, color: T.info ? T.info + "CC" : "#1E40AF", margin: 0 }}>
          <strong style={{ color: T.info || "#1D4ED8" }}>Resolução CVM 30</strong> — O formulário de
          onboarding é preenchido pelo próprio cliente. O consultor revisa, verifica a adequação ao
          perfil e aprova o suitability antes de qualquer recomendação de investimento.
        </p>
      </div>
    </div>
  );
}

AdminOnboardingTab.propTypes = {
  dynamicClients: PropTypes.array.isRequired,
  suitabilityDB: PropTypes.object.isRequired,
  setSelectedClient: PropTypes.func.isRequired,
  setEditOnboarding: PropTypes.func.isRequired,
  onApprove: PropTypes.func,
  T: PropTypes.object.isRequired,
};
